/**
 * Contraste de texto sobre a cor escolhida para o grupo da tabela.
 *
 * A cor vem de um `<input type="color">`, entao pode ser qualquer coisa entre
 * um azul-marinho e um ciano fluorescente. Fixar o texto em branco funciona so
 * na metade escura da paleta — na metade clara o nome da tabela some. Aqui a
 * tinta e escolhida pela razao de contraste da WCAG, a mesma que o navegador
 * usaria para julgar acessibilidade.
 */

export const DARK_INK = '#0B1220'
export const LIGHT_INK = '#FFFFFF'

type Rgb = readonly [number, number, number]

const HEX_SHORT = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i
const HEX_LONG = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i
const RGB_FN = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i

export function parseColor(input: string): Rgb | null {
  const value = input.trim()

  const short = HEX_SHORT.exec(value)
  if (short) {
    return [
      Number.parseInt(`${short[1]}${short[1]}`, 16),
      Number.parseInt(`${short[2]}${short[2]}`, 16),
      Number.parseInt(`${short[3]}${short[3]}`, 16),
    ]
  }

  const long = HEX_LONG.exec(value)
  if (long) {
    return [
      Number.parseInt(long[1], 16),
      Number.parseInt(long[2], 16),
      Number.parseInt(long[3], 16),
    ]
  }

  const fn = RGB_FN.exec(value)
  if (fn) {
    const channels = [Number(fn[1]), Number(fn[2]), Number(fn[3])]
    if (channels.every((channel) => Number.isFinite(channel))) {
      return [channels[0], channels[1], channels[2]] as Rgb
    }
  }

  return null
}

/** Luminancia relativa da WCAG 2.x (0 = preto, 1 = branco). */
export function relativeLuminance([r, g, b]: Rgb): number {
  const linear = [r, g, b].map((channel) => {
    const ratio = Math.min(Math.max(channel, 0), 255) / 255
    return ratio <= 0.03928 ? ratio / 12.92 : ((ratio + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2]
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const luminanceA = relativeLuminance(a)
  const luminanceB = relativeLuminance(b)
  const lighter = Math.max(luminanceA, luminanceB)
  const darker = Math.min(luminanceA, luminanceB)
  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * Tinta legivel sobre `background`. Cor desconhecida cai no branco, que era o
 * comportamento anterior — nao piora nada que ja funcionava.
 */
export function readableInk(background: string): string {
  const rgb = parseColor(background)
  if (!rgb) return LIGHT_INK

  const onDark = contrastRatio(rgb, parseColor(DARK_INK)!)
  const onLight = contrastRatio(rgb, parseColor(LIGHT_INK)!)
  return onDark >= onLight ? DARK_INK : LIGHT_INK
}

/**
 * Vela translucida para botoes/hover sobre `background`: clareia fundos escuros
 * e escurece os claros, entao a mesma regra serve para a paleta inteira.
 */
export function inkOverlay(background: string, alpha: number): string {
  return readableInk(background) === DARK_INK
    ? `rgba(0,0,0,${alpha})`
    : `rgba(255,255,255,${alpha})`
}
